import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import {
  AiAgentSession,
  AiAgentSessionOrderByInput,
  AiAgentSessionWhereInput,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { FiltersButton } from 'src/components/general/button/filters-button';
import { TableProps } from 'src/components/general/display/table';
import { TablePagination } from 'src/components/general/display/table-pagination';
import { TableLayoutPlaceholder } from 'src/components/general/feedback/loading-placeholders';
import {
  getSortDirectionByOrder,
  SorterData,
  TableSortData,
} from 'src/components/general/table-helpers';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { SessionOverviewDrawer } from 'src/components/pages/ai-agents/agents/sessions/components/session-overview-drawer';
import {
  SessionsFilters,
  sessionsFilterSchema,
  SessionsFiltersFormValues,
  toSessionsWhere,
} from 'src/components/pages/ai-agents/agents/sessions/components/sessions-filters';
import { SessionsTable } from 'src/components/pages/ai-agents/agents/sessions/components/sessions-table';
import { AI_AGENT_SESSIONS_QUERY } from 'src/components/pages/ai-agents/agents/sessions/queries';
import { KnowledgeEmptyState } from 'src/components/pages/ai-agents/components/knowledge-tabs';
import {
  AiAgentsListVariables,
  useAiAgentsListQueryParams,
} from 'src/components/pages/ai-agents/hooks/use-ai-agents-list-query-params';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useVisible } from 'src/hooks/use-visible';

type SessionsVariables = AiAgentsListVariables<AiAgentSessionOrderByInput> & {
  aiAgentId?: string;
  where?: AiAgentSessionWhereInput;
};

const DEFAULT_SORT_DATA: TableSortData = { key: 'createdAt', direction: 'DESC' };

const SessionsContainer = styled(FlexColumn)`
  flex: 1;
  min-width: 0;
  padding: 0 15px;

  ${up('md')} {
    margin: 10px 0 0 10px;
    padding: 0;
  }
`;

const SessionsHeader = styled(FlexRow)`
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;

  ${up('md')} {
    margin-bottom: 20px;
    padding: 0;
  }
`;

const EmptyCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 6px;
`;

const SessionsCount = styled.span`
  color: ${({ theme }) => theme.colors.storm};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 15px;
  line-height: 19px;

  strong {
    color: ${({ theme }) => theme.colors.gogo};
    font-weight: normal;
  }
`;

export const Sessions = memo<{ aiAgentId: string }>(({ aiAgentId }) => {
  const { t } = useTranslation(['aiAgents']);
  const handleGraphQLError = useHandleGraphQLError();
  const filtersVisibilityHandler = useVisible();
  const overviewVisibilityHandler = useVisible();
  const { setVisible: setOverviewVisible } = overviewVisibilityHandler;
  const [overviewSessionId, setOverviewSessionId] = useState<string | null>(null);

  const {
    handleNextPage,
    handlePreviousPage,
    handleSortChange,
    sortData,
    variables,
    filters,
    hasActiveFilters,
  } = useAiAgentsListQueryParams({
    defaultSortData: DEFAULT_SORT_DATA,
    resolveOrderBy: ({ direction }): AiAgentSessionOrderByInput => ({
      createdAt: direction,
    }),
    filtersResolver: sessionsFilterSchema.parse,
    resolveExtraVariables: (filters: SessionsFiltersFormValues) => ({
      aiAgentId,
      where: toSessionsWhere(filters),
    }),
  });

  const { data, loading } = useQuery<Pick<Query, 'aiAgents'>, SessionsVariables>(
    AI_AGENT_SESSIONS_QUERY,
    { variables, onError: handleGraphQLError },
  );

  const handleTableChange = useCallback<
    NonNullable<TableProps<AiAgentSession>['onChange']>
  >(
    (pagination, tableFilters, sorter) => {
      const { columnKey, order } = sorter as SorterData<AiAgentSession>;
      handleSortChange({ key: columnKey, direction: getSortDirectionByOrder(order) });
    },
    [handleSortChange],
  );

  const openOverview = useCallback(
    ({ id }: AiAgentSession) => {
      setOverviewSessionId(id);
      setOverviewVisible(true);
    },
    [setOverviewVisible],
  );

  const sessions = data?.aiAgents.getAIAgent?.sessions;

  if (!sessions || loading) {
    return (
      <SessionsContainer data-lgg-id="ai-agent-sessions-section">
        <TableLayoutPlaceholder />
      </SessionsContainer>
    );
  }

  const { hasPreviousPage, hasNextPage, startCursor, endCursor } = sessions.pageInfo;

  return (
    <SessionsContainer data-lgg-id="ai-agent-sessions-section">
      <SessionsHeader>
        <SessionsCount data-lgg-id="ai-agent-sessions-count">
          <strong>{sessions.totalCount}</strong>{' '}
          {t('aiAgents:pages.agentDetails.sessions.count', {
            count: sessions.totalCount,
          })}
        </SessionsCount>
        <FiltersButton
          data-lgg-id="ai-agent-sessions-filters-button"
          filtersVisibilityHandler={filtersVisibilityHandler}
          hasActiveFilters={hasActiveFilters}
        />
      </SessionsHeader>
      {sessions.totalCount === 0 ? (
        <EmptyCard>
          <KnowledgeEmptyState
            testId="ai-agent-sessions-empty"
            icon="aiSessionChat"
            title={t('aiAgents:pages.agentDetails.sessions.empty.title')}
            message={
              hasActiveFilters
                ? t('aiAgents:pages.agentDetails.sessions.empty.filtered')
                : t('aiAgents:pages.agentDetails.sessions.empty.message')
            }
          />
        </EmptyCard>
      ) : (
        <SessionsTable
          data={sessions.nodes}
          sortData={sortData}
          onChange={handleTableChange}
          onViewOverview={openOverview}
        />
      )}
      <TablePagination
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        onLoadPreviousPage={() => {
          if (hasPreviousPage && startCursor) {
            handlePreviousPage({ startCursor });
          }
        }}
        onLoadNextPage={() => {
          if (hasNextPage && endCursor) {
            handleNextPage({ endCursor });
          }
        }}
      />
      <SessionsFilters
        visible={filtersVisibilityHandler.visible}
        onClose={filtersVisibilityHandler.close}
        filters={filters}
      />
      {overviewSessionId && (
        <SessionOverviewDrawer
          sessionId={overviewSessionId}
          visibilityHandler={overviewVisibilityHandler}
        />
      )}
    </SessionsContainer>
  );
});
